/**
 * Organization-scoped feature flags.
 *
 * Each set contains the organization IDs for which a feature is enabled
 * (or disabled, for kill-switch style flags). An empty set means the flag
 * applies to no organizations.
 */

/**
 * Parse a comma-separated env var into a Set of trimmed, non-empty org IDs.
 * `REACT_APP_*` env vars are inlined at build time by webpack, so each call
 * evaluates to a constant Set once at module load.
 */
const parseOrgIdSet = (raw: string | undefined): Set<string> =>
  new Set(
    (raw ?? '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean),
  );

/** Organization IDs for which the QC page is disabled for non-100ms users (from env var). */
export const QC_DISABLED_ORG_IDS: Set<string> = parseOrgIdSet(
  process.env.REACT_APP_QC_DISABLED_ORG_IDS,
);

/** Organization IDs for which the Analytics page is disabled for non-100ms users (from env var). */
export const ANALYTICS_DISABLED_ORG_IDS: Set<string> = parseOrgIdSet(
  process.env.REACT_APP_ANALYTICS_DISABLED_ORG_IDS,
);

/** Organization IDs for which the AE / PQC Analytics page is disabled for non-100ms users (from env var). */
export const CASES_ANALYTICS_DISABLED_ORG_IDS: Set<string> = parseOrgIdSet(
  process.env.REACT_APP_CASES_ANALYTICS_DISABLED_ORG_IDS,
);

// Kill-switch for the frontend analytics SDK (event tracking), separate from the analytics PAGE flag.
export const ANALYTICS_SDK_DISABLED_ORG_IDS: Set<string> = parseOrgIdSet(
  process.env.REACT_APP_ANALYTICS_SDK_DISABLED_ORG_IDS,
);

/** Organization IDs that have the My Tasks page enabled (from env var). Empty = no org. */
const TASKS_ENABLED_ORG_IDS: Set<string> = parseOrgIdSet(
  process.env.REACT_APP_TASKS_ENABLED_ORG_IDS,
);

/** True when My Tasks is enabled for this org. UI-only gate: the API itself is not restricted. */
export const isMyTasksEnabledForOrg = (
  organizationId: string | null | undefined,
  enabledOrgIds: Set<string> = TASKS_ENABLED_ORG_IDS,
): boolean => !!organizationId && enabledOrgIds.has(organizationId);

/** Organization IDs that have the All Tasks page enabled (from env var). Empty = no org. */
const ALL_TASKS_ENABLED_ORG_IDS: Set<string> = parseOrgIdSet(
  process.env.REACT_APP_ALL_TASKS_ENABLED_ORG_IDS,
);

/**
 * True when All Tasks is enabled for this org. UI-only gate: ListOrganizationManualTasks
 * scopes its results by permission on its own.
 */
export const isAllTasksEnabledForOrg = (
  organizationId: string | null | undefined,
  enabledOrgIds: Set<string> = ALL_TASKS_ENABLED_ORG_IDS,
): boolean => !!organizationId && enabledOrgIds.has(organizationId);
